import React, { useId } from 'react';
import { PiCheckBold } from 'react-icons/pi';
import type {
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Link } from 'src/modules/shared/components/atoms/links/link';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import { buildWebsiteTemplatePreviewUrl } from '../../utils/template-preview.util';
import type { WebsiteTemplateBadge } from '../../utils/website-template-catalog.util';
import { WebsiteTemplateLiveThumbnail } from './website-template-live-thumbnail';

/** Props for one design card. */
interface WebsiteTemplateCardProps {
  /** Design shown by the card. */
  template: WebsiteTemplateRegistryTemplateSummary;
  /** Draft whose brand personalizes the thumbnail and example link. */
  form: WebsiteUpsertRequest;
  /** Highlight next to the design name. */
  badge?: WebsiteTemplateBadge;
  /** Whether the live site uses this design. */
  inUse: boolean;
  /** Whether the draft uses this design. */
  selected: boolean;
  /** Whether design changes are blocked right now. */
  disabled: boolean;
  /** Called when the user tries this design. */
  onSelect: () => void;
}

/**
 * One design in the picker: a live thumbnail, the name with its tags, the
 * pitch, and actions to try the design or open its example.
 *
 * @param props - Design, draft, tags, and selection state.
 * @returns The design card.
 */
export const WebsiteTemplateCard: React.FC<WebsiteTemplateCardProps> = ({
  template,
  form,
  badge,
  inUse,
  selected,
  disabled,
  onSelect,
}) => {
  const { t } = useI18n();
  const titleId = useId();
  const exampleUrl = buildWebsiteTemplatePreviewUrl({ template, form });

  return (
    <article
      aria-labelledby={titleId}
      className={classes(
        'flex min-w-0 flex-col overflow-hidden rounded-lg border bg-white shadow-sm transition',
        selected
          ? 'border-primary-500 ring-1 ring-primary-500'
          : 'border-neutral-200 hover:border-neutral-300'
      )}
    >
      <div className="relative">
        <WebsiteTemplateLiveThumbnail template={template} form={form} />
        <button
          type="button"
          tabIndex={-1}
          className="absolute inset-0 cursor-pointer disabled:cursor-default"
          aria-label={t('websites.settings.design.actions.tryNamed', {
            templateName: template.name,
          })}
          onClick={onSelect}
          disabled={disabled || selected}
        />
      </div>
      <div className="flex flex-1 flex-col gap-2 border-t border-neutral-100 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Text as="h4" size="h6" weight="medium">
            <span id={titleId}>{template.name}</span>
          </Text>
          {badge === 'recommended' ? (
            <StatusPill
              label={t('websites.settings.design.badge.recommended')}
              variant="info"
            />
          ) : null}
          {badge === 'new' ? (
            <StatusPill
              label={t('websites.settings.design.badge.new')}
              variant="neutral"
            />
          ) : null}
          {inUse ? (
            <StatusPill
              label={t('websites.settings.design.badge.inUse')}
              variant="success"
              className="ml-auto"
            />
          ) : null}
        </div>
        {template.description ? (
          <Text size="small" color="secondary" className="flex-1">
            {template.description}
          </Text>
        ) : (
          <span className="flex-1" />
        )}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {selected ? (
            <Button size="sm" variant="secondary" appearance="soft" disabled>
              <PiCheckBold aria-hidden="true" className="h-3.5 w-3.5" />
              {t('websites.settings.design.selected')}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              appearance="outline"
              onClick={onSelect}
              disabled={disabled}
            >
              {t('websites.settings.design.actions.try')}
            </Button>
          )}
          {exampleUrl ? (
            <Link
              href={exampleUrl}
              size="small"
              variant="neutral"
              className="px-1"
            >
              {t('websites.settings.design.actions.viewExample')}
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  );
};
